import React, { useEffect, useRef, useState } from "react";
import styles from "./ReconciliationOverview.module.css";

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

const Donut = ({ segments, size = 200, thickness = 24 }) => {
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  // compute offsets for stacked segments
  let acc = 0;

  return (
    <svg width={size} height={size} className={styles.donut}>
      <g transform={`translate(${size / 2}, ${size / 2})`}>
        {/* background ring */}
        <circle
          r={radius}
          fill="transparent"
          stroke="#eef6f2"
          strokeWidth={thickness}
        />

        {segments.map((seg, idx) => {
          const len = circumference * (seg.value / seg.total);
          const dasharray = `${len} ${circumference - len}`;
          const offset = -circumference * (acc / seg.total);
          acc += seg.value;
          return (
            <circle
              key={idx}
              r={radius}
              fill="transparent"
              stroke={seg.color}
              strokeWidth={thickness}
              strokeLinecap="round"
              strokeDasharray={dasharray}
              strokeDashoffset={offset}
              transform="rotate(-90)"
            />
          );
        })}
      </g>
    </svg>
  );
};

const ReconciliationOverview = ({ initial = null }) => {
  const defaultData = {
    matched: 118420,
    unmatched: 6430,
    exceptions: 824,
  };

  const [data, setData] = useState(initial || defaultData);
  const total = data.matched + data.unmatched + data.exceptions;

  // simulate realtime updates with small variations
  useEffect(() => {
    let mounted = true;
    const id = setInterval(() => {
      if (!mounted) return;
      // apply small random delta to matched/unmatched keeping total roughly same
      const delta = Math.round((Math.random() - 0.5) * 200);
      setData((d) => {
        const matched = clamp(d.matched + delta, 0, d.matched + 500);
        const remaining = Math.max(0, d.matched + d.unmatched + d.exceptions - matched);
        // split remaining proportionally
        const ratio = remaining / (d.unmatched + d.exceptions || 1);
        const unmatched = Math.round(d.unmatched * ratio);
        const exceptions = Math.round(remaining - unmatched);
        return { matched, unmatched, exceptions };
      });
    }, 3500);

    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  const percent = ((data.matched / total) * 100).toFixed(1);

  const segments = [
    { value: data.matched, color: "#0b7a4a", total },
    { value: data.unmatched, color: "#d9e3e0", total },
    { value: data.exceptions, color: "#f1f3f5", total },
  ];

  return (
    <div className={styles.card}>
      <h3 className={styles.title}>Reconciliation Overview</h3>

      <div className={styles.content}>
        <div className={styles.left}>
          <div className={styles.donutWrapper}>

          <Donut segments={segments} size={180} thickness={20} />
          </div>
          <div className={styles.centerLabel}>
            <div className={styles.percent}>{percent}%</div>
            <div className={styles.labelSmall}>Reconciled</div>
          </div>
        </div>

        <div className={styles.right}>
          <ul className={styles.legend}>
            <li>
              <span className={styles.dot} style={{ background: "#0b7a4a" }} />
              <div className={styles.legendText}>
                <div>Matched</div>
                <div className={styles.legendValue}>{data.matched.toLocaleString()}</div>
              </div>
            </li>

            <li>
              <span className={styles.dot} style={{ background: "#d9e3e0" }} />
              <div className={styles.legendText}>
                <div>Unmatched</div>
                <div className={styles.legendValue}>{data.unmatched.toLocaleString()}</div>
              </div>
            </li>

            <li>
              <span className={styles.dot} style={{ background: "#f1f3f5" }} />
              <div className={styles.legendText}>
                <div>Exceptions</div>
                <div className={styles.legendValue}>{data.exceptions.toLocaleString()}</div>
              </div>
            </li>
          </ul>

          <button className={styles.reportBtn}>View Full Report</button>
        </div>
      </div>
    </div>
  );
};

export default ReconciliationOverview;
